<script setup lang="ts">
import { computed, ref } from "vue";

import MenuItem from "./MenuItem.vue";

import LogoImage from "@/assets/images/LogoImage.vue";
import SmallLogo from "@/assets/images/pages/SmallLogo.vue";
import { routesForMenu } from "@/router/modules.ts";
import { useFiltersStore } from "@/store/filters/index.ts";

withDefaults(
  defineProps<{
    isOpen: boolean
    isSmallMenu: boolean
    mobileMenu: boolean
  }>(),
  {
    isOpen: false,
    isSmallMenu: false,
  },
);

const filterStore = useFiltersStore();

const emit = defineEmits(["showMenu", "handleSmallMenu"]);

const menuToggleHandler = ref<boolean>(true);

const menuList = computed(() => routesForMenu);

const toggleMenu = (showMenu: boolean) => {
  if (window.innerWidth <= 767) {
    return;
  }
  menuToggleHandler.value = showMenu;
  filterStore.handleMobileMenu(false);
  emit("showMenu", showMenu);
};

const closeMobileMenu = () => {
  menuToggleHandler.value = false;
  filterStore.handleMobileMenu(false);
  emit("showMenu", false);
};
</script>

<template>
  <nav
    class="menu"
    :class="{
      'small-menu': isSmallMenu && !mobileMenu,
      'mobile-menu': mobileMenu,
      'mobile-closed': !mobileMenu,
    }"
    @mouseover="toggleMenu(false)"
    @mouseleave="toggleMenu(true)"
  >
    <section
      class="mt-[20px] h-[60px] p-2 flex justify-center items-center rounded-md"
    >
      <div class="w-full flex justify-between md:justify-center items-center">
        <LogoImage
          class="w-full h-full max-w-[90%]"
          :class="{ hidden: isSmallMenu }"
        />
        <div
          class="flex md:hidden"
          @click="closeMobileMenu"
        >
          <feather-icon
            type="x"
            class="text-secondaryText cursor-pointer hover:text-primary duration-500 transition-all"
            stroke-width="2"
            size="34"
          />
        </div>
        <SmallLogo :class="{ hidden: !isSmallMenu }" />
      </div>
    </section>
    <section class="overflow-y-auto overflow-x-hidden">
      <MenuItem
        v-for="(item, index) in menuList"
        :key="index"
        :index="index"
        :route-item="item"
        :depth="0"
        :small-menu="isSmallMenu"
        :is-open="menuToggleHandler"
        class="hide-expand"
      />
    </section>
  </nav>
</template>

<style lang="scss" scoped>
.menu {
  @apply shadow-xl dark:shadow-slate-900/50 bg-navigationBg text-secondaryText fixed top-0 transition-all duration-300 z-[101] flex flex-col gap-2 ml-2 mt-3 mb-2 rounded-lg w-screen md:w-[320px];
  position: fixed;
  height: calc(100vh - 30px);
  &.small-menu {
    overflow: inherit;
    width: 100px;
  }
}

.mobile-menu {
  @apply left-0 fixed h-full;
}

.mobile-closed {
  @apply left-[-110%] md:left-0 fixed;
}
</style>
